import React, { useEffect } from "react";
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerCloseButton,
  Flex,
  Text,
  Skeleton,
  Box,
} from "@chakra-ui/react";
import { FaMapMarkedAlt } from "react-icons/fa";
import { GuidesMap } from "../Components/GuidesMap";
import { bottomDrawerSize } from "./drawerSizing";
import { useDrawerSettled } from "./useDrawerSettled";

// The map lives in a drawer rather than a tab so the tab strip stays purely
// for reading content. Tapping a pin opens the guide's own drawer on top, and
// closing that lands the guest back on the map. `isCovered` must be true while
// any such drawer is open (see the scroll-lock note inside).
export const GuidesMapDrawer = ({
  isOpen,
  onClose,
  room,
  guides,
  loading,
  onGuideSelect,
  isCovered = false,
}) => {
  const settled = useDrawerSettled(isOpen);

  // Chakra only gives the first drawer in a stack a scroll lock
  // (`enabled: index === 1 && blockScrollOnMount`), and that lock treats
  // every other drawer as "outside" itself — so a guide opened from a pin
  // sits on top of a lock that swallows its scrolling. While something is
  // open on top, this drawer lets go of its lock (below) and the page lock is
  // held by hand here instead, so the top drawer scrolls freely and the page
  // behind still can't move. Everything is restored when the cover closes.
  useEffect(() => {
    if (!isOpen || !isCovered) return undefined;

    const html = document.documentElement;
    const body = document.body;
    const previous = {
      bodyOverflow: body.style.overflow,
      htmlOverflow: html.style.overflow,
      bodyPaddingRight: body.style.paddingRight,
    };

    // Hiding the scrollbar would shift the page sideways on desktop.
    const scrollbarGap = window.innerWidth - html.clientWidth;
    body.style.overflow = "hidden";
    html.style.overflow = "hidden";
    if (scrollbarGap > 0) body.style.paddingRight = `${scrollbarGap}px`;

    return () => {
      body.style.overflow = previous.bodyOverflow;
      html.style.overflow = previous.htmlOverflow;
      body.style.paddingRight = previous.bodyPaddingRight;
    };
  }, [isOpen, isCovered]);

  return (
  <Drawer
    placement="bottom"
    onClose={onClose}
    isOpen={isOpen}
    size="full"
    blockScrollOnMount={!isCovered}
  >
    <DrawerOverlay />
    <DrawerContent
      bg="#1A202C"
      color="white"
      // A fixed height (not just a cap) so the map can fill whatever the
      // header leaves, instead of collapsing to its content. `fill`: the map
      // wants every pixel the browser leaves visible — dvh keeps the close
      // button on screen even with the toolbar showing, so there's no need
      // to hold height back as a margin of safety.
      {...bottomDrawerSize({ mobile: 92, desktop: 84, fixed: true, fill: true })}
      overflow="hidden"
      borderTopRadius="20px"
      display="flex"
      flexDirection="column"
      maxW={{ base: "100%", md: "880px" }}
      mx="auto"
      mb={{ md: 6 }}
      borderRadius={{ md: "24px" }}
    >
      <DrawerHeader
        px={{ base: 4, md: 6 }}
        py={4}
        borderBottom="1px solid"
        borderColor="rgba(255, 255, 255, 0.1)"
        flexShrink={0}
      >
        <Flex alignItems="center" justifyContent="space-between">
          <Flex alignItems="center" gap={2}>
            <FaMapMarkedAlt color="#817EFF" />
            <Text fontSize={{ base: "16px", md: "18px" }} fontWeight="700">
              Map
            </Text>
          </Flex>
          <DrawerCloseButton pos="relative" top={0} right={0} />
        </Flex>
      </DrawerHeader>

      <DrawerBody p={0} overflow="hidden" flex={1} minH={0}>
        {/* Mounted only while open (so the map and its geocoding never run
            for guests who don't ask for it) and only after the slide-in has
            finished — initializing Google Maps mid-animation makes the sheet
            stutter. */}
        {isOpen && settled ? (
          <GuidesMap
            room={room}
            guides={guides}
            loading={loading}
            onGuideSelect={onGuideSelect}
          />
        ) : (
          <Skeleton h="100%" />
        )}
      </DrawerBody>

      {/* Backdrop for a drawer opened on top (a guide from a pin). Chakra
          gives that drawer its own overlay, but it only shows over the thin
          strip of this sheet left uncovered — mostly the already-dark header
          — so it barely reads. Dimming this sheet itself makes the stack
          obvious. Taps fall through to the top drawer's overlay, which
          closes it. */}
      <Box
        position="absolute"
        inset={0}
        bg="blackAlpha.600"
        borderRadius="inherit"
        opacity={isCovered ? 1 : 0}
        transition="opacity 0.2s ease"
        pointerEvents="none"
        zIndex={2}
      />
    </DrawerContent>
  </Drawer>
  );
};

export default GuidesMapDrawer;
